import { useEffect, useState } from "react";
import { ScanEye, Info } from "lucide-react";
import { api } from "../../api";
import { PICTOGRAM_CATALOGUE, PICTO_INDEX, getPictoIcon } from "./pictograms";

/**
 * Label Reader.
 *
 * Left column shows the pictograms that apply to the active substance
 * (small badge grid). Right column is the full catalogue of every
 * pictogram used in this hub — the user clicks any diamond to see what
 * that label MEANS and which of our four chemicals it appears on.
 *
 * Every icon comes from `pictograms.js`, so the two sides share visual language.
 */
export default function Ghs({ chemKey, dark }) {
  const [items, setItems] = useState([]); // pictograms for active substance (from API)
  const [selected, setSelected] = useState(PICTOGRAM_CATALOGUE[0].name);
  const [chemicals, setChemicals] = useState([]);
  const [chemPictos, setChemPictos] = useState({}); // { hcl: [names], h2so4: [names], ... }

  useEffect(() => {
    api.getGhs(chemKey).then((d) => setItems(d.items || []));
  }, [chemKey]);

  // Load the pictogram list for every chemical ONCE so we can compute carriers
  // client-side without hitting the network on every catalogue click.
  useEffect(() => {
    let alive = true;
    api.listChemicals().then(async (chems) => {
      if (!alive) return;
      setChemicals(chems);
      const results = await Promise.all(
        chems.map((c) =>
          api.getGhs(c.key).then((d) => [c.key, (d.items || []).map((it) => it.name)])
        )
      );
      if (!alive) return;
      setChemPictos(Object.fromEntries(results));
    });
    return () => { alive = false; };
  }, []);

  const activePictoNames = new Set(items.map((i) => i.name));
  const detail = PICTO_INDEX[selected];

  const carriers = chemicals.filter((c) => (chemPictos[c.key] || []).includes(selected));

  return (
    <section className={"panel" + (dark ? " dark" : "")} id="ghs" data-testid="section-ghs">
      <div className="wrap">
        <div className="panel-head">
          <div className="eyebrow">Tool · GHS / NFPA decoder</div>
          <h2>Pictograms &amp; label reader</h2>
          <p>
            Left: the pictograms printed on our tanker labels for the active substance. Right: the full
            catalogue of pictograms you might see on any chemical drum or tanker — click any diamond to
            see what it means and which of our four substances carry it.
          </p>
        </div>

        <div className="ghs-layout">
          <div>
            <div className="sub-panel-head" style={{ marginBottom: 12 }}>
              <div className="eyebrow">Active substance</div>
            </div>
            <div className="ghs-grid" data-testid="ghs-grid">
              {items.map((g, i) => {
                const Icon = getPictoIcon(g.name);
                return (
                  <div className="ghs-card" key={i} data-testid={`ghs-card-${i}`}>
                    <div className="ghs-diamond">
                      <span className="ghs-diamond-icon">
                        <Icon size={22} strokeWidth={2} />
                      </span>
                    </div>
                    <div className="name">{g.name}</div>
                    <div className="sub">{g.sub}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <div className="sub-panel-head" style={{ marginBottom: 12 }}>
              <div className="eyebrow">
                <ScanEye size={13} style={{ verticalAlign: "middle" }} /> Label reader
              </div>
              <p className="note">
                Tap any pictogram to see what that label means and where you&apos;ll encounter it.
              </p>
            </div>

            <div className="picto-catalogue" data-testid="picto-catalogue">
              {PICTOGRAM_CATALOGUE.map((p) => {
                const Icon = p.icon;
                const isOn = selected === p.name;
                const isActive = activePictoNames.has(p.name);
                const testid = `picto-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`;
                return (
                  <button
                    key={p.name}
                    type="button"
                    className={
                      "picto-catalogue-item" +
                      (isOn ? " on" : "") +
                      (isActive ? " active-sub" : "")
                    }
                    onClick={() => setSelected(p.name)}
                    data-testid={testid}
                    title={p.name}
                  >
                    <div className="picto-mini-diamond" style={{ borderColor: p.tone }}>
                      <span className="picto-mini-icon" style={{ color: p.tone }}>
                        <Icon size={20} strokeWidth={2} />
                      </span>
                    </div>
                    <div className="picto-catalogue-name">{p.name}</div>
                    {isActive && (
                      <span
                        className="picto-active-badge"
                        title="Applies to active substance"
                        data-testid={`picto-active-badge-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      >
                        On active
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {detail && (
              <div className="picto-detail" data-testid="picto-detail">
                <div className="picto-detail-head">
                  <div className="picto-mini-diamond lg" style={{ borderColor: detail.tone }}>
                    <span className="picto-mini-icon" style={{ color: detail.tone }}>
                      <detail.icon size={26} strokeWidth={2} />
                    </span>
                  </div>
                  <div>
                    <div className="eyebrow" style={{ color: detail.tone }}>
                      <Info size={12} style={{ verticalAlign: "middle" }} /> Pictogram
                    </div>
                    <h3 className="picto-detail-name" data-testid="picto-detail-name">{detail.name}</h3>
                  </div>
                </div>
                <div className="picto-detail-body">
                  <div>
                    <div className="mono picto-detail-label">Meaning</div>
                    <p data-testid="picto-detail-meaning">{detail.meaning}</p>
                  </div>
                  <div>
                    <div className="mono picto-detail-label">Where you&apos;ll see it</div>
                    <p>{detail.when}</p>
                  </div>
                  <div>
                    <div className="mono picto-detail-label">Applies to</div>
                    {carriers.length === 0 ? (
                      <p className="note" data-testid="picto-detail-carriers-empty">
                        None of our four substances carry this label.
                      </p>
                    ) : (
                      <div className="picto-carriers" data-testid="picto-detail-carriers">
                        {carriers.map((c) => (
                          <span key={c.key} className="picto-carrier-chip mono" data-testid={`picto-carrier-${c.key}`}>
                            {c.formula} · {c.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
